import { useMemo } from 'react';
import { useAppStore } from '../store';
import { useReportStore } from '../store/reportStore';
import { getCategoryDescendants } from '../services/categoryService';
import {
  calculateTransactionBins,
  type TransactionBin,
} from '../utils/transactionBins';

export interface TransactionSizeDistributionData {
  bins: TransactionBin[];
  maxCount: number;
  maxValue: number;
}

export function useTransactionSizeDistribution(): TransactionSizeDistributionData {
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);
  const dateRange = useReportStore((state) => state.dateRange);
  const selectedCategoryIds = useReportStore(
    (state) => state.selectedCategoryIds
  );

  return useMemo(() => {
    const allowedCategoryIds = new Set<string>();
    if (selectedCategoryIds.length > 0) {
      selectedCategoryIds.forEach((id) => {
        allowedCategoryIds.add(id);
        const descendants = getCategoryDescendants(categories, id);
        descendants.forEach((d) => allowedCategoryIds.add(d.id));
      });
    }

    const filteredTransactions = transactions.filter((t) => {
      if (t.type !== 'expense') return false;
      if (t.excludedFromBudget) return false;

      const transactionDate = t.date;
      if (
        transactionDate < dateRange.start ||
        transactionDate > dateRange.end
      ) {
        return false;
      }

      if (
        allowedCategoryIds.size > 0 &&
        t.categoryId &&
        !allowedCategoryIds.has(t.categoryId)
      ) {
        return false;
      }

      return true;
    });

    const bins = calculateTransactionBins(filteredTransactions);

    const maxCount = Math.max(...bins.map((b) => b.count), 0);
    const maxValue = Math.max(...bins.map((b) => b.totalValue), 0);

    return {
      bins,
      maxCount,
      maxValue,
    };
  }, [transactions, categories, dateRange, selectedCategoryIds]);
}
